import React, { Fragment, useState } from 'react';
import { Field, FieldArray, Form, getIn } from 'formik';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormInputField from '../../../common/components/formFields/FormInputField';
import PostcodeSelectField from '../../../register/forms/postcode/PostcodeSelectField';
import CommonButton from '../../../common/components/CommonButton';
import { saveOrganizationDetails } from '../../SettingsActions';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import { DealerLocationStatusTypes } from '../../../constants/DealerLocationStatusTypes';
import redPlusIcon from '../../../assets/img/icons/red-plus.svg';
import { CommonSweetAlertVariants } from '../../../constants/CommonSweetAlertVariants';
import CommonSweetAlert from '../../../common/components/CommonSweetAlert';
import SettingsSectionHeader from '../../SettingsSectionHeader';
import AddAddressModal from '../modals/AddAddressModal';
import phoneIcon from '../../../assets/img/icons/phone.svg';
import worldIcon from '../../../assets/img/icons/worldicon.svg';
import trashIcon from '../../../assets/img/icons/trash-red.svg';
import closeIcon from '../../../assets/img/icons/close.svg';
import '../../../assets/css/componentStyles/InformationForm.css';

function InformationsForm({ values, setSubmitting, isValid, setFieldValue }) {
  const dispatch = useDispatch();
  const organizationDetails = useSelector((state) =>
    state.myOrganization.get('organizationDetails'),
  );
  const [localLoading, setLocalLoading] = useState(false);
  const isOwner = useSelector((state) => state.auth.get('isOwner'));
  const prevAdditionalLocation = organizationDetails?.additionalLocation;
  const [locationIdsToDelete, setLocationIdsToDelete] = useState([]);
  const [idDelLocation, setIdDelLocation] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleDeleteAdditionalLocation = (location, arrayHelpers, index) => {
    if (location && values) {
      setLocationIdsToDelete((prevState) => [...prevState, location?.id]);
      arrayHelpers.remove(index);
    }
  };

  const handleSaveAddress = (newAddress) => {
    const toUpdate = [...(values.additionalLocation || []), newAddress];
    setFieldValue('additionalLocation', toUpdate);
  };

  const handleSaveOrganizationDetails = async () => {
    if (isValid) {
      setSubmitting(true);
      setLocalLoading(true);
      try {
        await dispatch(
          saveOrganizationDetails(values, prevAdditionalLocation, locationIdsToDelete),
        );
      } finally {
        setSubmitting(false);
        setLocalLoading(false);
      }
    }
  };

  const isAdditionalLocationDisabled = (location) => !!(!isOwner || location?.id);
  return (
    <div>
      <Form autoComplete="off" className="informations-form">
        <Row className="mt-24">
          <Col md={4}>
            <FormInputField
              name="companyName"
              label="Company Name"
              disabled
              labelVariant="bordered"
              required
            />
          </Col>
          <Col md={4}>
            <FormInputField
              name="tradeName"
              label="Trade Name"
              disabled
              labelVariant="bordered"
              initialValue={organizationDetails?.tradeName}
              required
            />
          </Col>
          <Col md={4}>
            <FormInputField
              name="vatNumber"
              label="VAT Registration Number"
              disabled
              labelVariant="bordered"
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <FormInputField
              name="mobileNumber"
              label="Mobile Number"
              labelVariant="bordered"
              required
              icon={phoneIcon}
              iconClassName="input-right-icon informations-form-icon"
              disabled={!isOwner}
            />
          </Col>
          <Col
            md={4}
            className={`${
              values?.landlinePhoneNumberIsPending && 'informations-form__pending-field'
            }`}
          >
            <FormInputField
              name="landlinePhoneNumber"
              label="Landline Number"
              disabled={!isOwner}
              labelVariant="bordered"
              icon={phoneIcon}
              iconClassName="input-right-icon informations-form-icon"
            />
          </Col>
          <Col
            md={4}
            className={`d-flex flex-column justify-content-center ${
              values?.landlinePhoneNumberIsPending ? 'mt-2' : ''
            } `}
          >
            <FormInputField
              name="website"
              label="Website address"
              disabled={!isOwner}
              labelVariant="bordered"
              icon={worldIcon}
              iconClassName="input-right-icon"
            />
          </Col>
        </Row>
        <Row
          className={`${
            values?.mainLocationIsPending && 'informations-form__pending-field'
          } mt-1`}
        >
          <Col md={12} className="mb-4">
            <SettingsSectionHeader header="Location Address" variant="secondary" />
          </Col>
          <Col md={6} lg={4}>
            <FormInputField
              name="location"
              label="Location Name"
              disabled={!isOwner}
              labelVariant="bordered"
              required
            />
          </Col>
          <Col md={6} lg={4}>
            <FormInputField
              name="address"
              label="Address"
              disabled={!isOwner}
              labelVariant="bordered"
              required
            />
          </Col>
          <Col md={6} lg={4}>
            <FormInputField
              name="address2"
              label="Address (line 2)"
              disabled={!isOwner}
              labelVariant="bordered"
            />
          </Col>
          <Col md={6} lg={4}>
            <FormInputField
              name="city"
              label="Town"
              disabled={!isOwner}
              labelVariant="bordered"
              required
            />
          </Col>
          <Col md={6} lg={4}>
            <FormInputField
              name="county"
              label="County"
              disabled={!isOwner}
              labelVariant="bordered"
              required
            />
          </Col>
          <Col md={6} lg={4}>
            <PostcodeSelectField
              name="postalCodeId"
              label="Postal code"
              loadRightAway={false}
              disabled={!isOwner}
              labelVariant="bordered"
            />
          </Col>
          {values?.mainLocation && (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <button type="button">
              <i className="far fa-trash-alt informations-form__trash-icon" />
            </button>
          )}
        </Row>
        <Row>
          <Col md={12}>
            <FieldArray
              name="additionalLocation"
              render={(arrayHelpers) => (
                <Row className="d-flex flex-column justify-content-center">
                  <div>
                    {values?.additionalLocation.map((location, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={index}>
                        <div className="line-break as-row mt-4" />
                        <Col
                          className="d-flex flex-row justify-content-between align-items-center mb-4"
                          id={`additional-location-${index}`}
                        >
                          <h5 className="d-inline-block font-weight-bold m-0">
                            Location Address {index + 1}
                          </h5>
                        </Col>
                        <Col
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          className={`mb-3 ${
                            location.status &&
                            location.status === DealerLocationStatusTypes.PENDING &&
                            'informations-form__pending-field py-24 px-8'
                          }`}
                        >
                          <Row className="informations-form-row">
                            <Col md={6} lg={4}>
                              <FormInputField
                                name={`additionalLocation.${index}.location`}
                                label="Location Name"
                                disabled={isAdditionalLocationDisabled(location)}
                                labelVariant="bordered"
                                required
                              />
                            </Col>
                            <Col md={6} lg={4}>
                              <Field name={`additionalLocation.${index}.address`}>
                                {({ form }) => {
                                  const error = getIn(
                                    form.errors,
                                    `additionalLocation.${index}.address`,
                                  );
                                  const touch = getIn(
                                    form.touched,
                                    `additionalLocation.${index}.address`,
                                  );
                                  return (
                                    <div>
                                      <FormInputField
                                        name={`additionalLocation.${index}.address`}
                                        invalid={!!touch && !!error}
                                        label="Address"
                                        disabled={isAdditionalLocationDisabled(location)}
                                        labelVariant="bordered"
                                        required
                                      />
                                      {touch && error ? (
                                        <label className="error-label">{error}</label>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              </Field>
                            </Col>
                            <Col md={6} lg={4}>
                              <Field name={`additionalLocation.${index}.address2`}>
                                {({ form }) => {
                                  const error = getIn(
                                    form.errors,
                                    `additionalLocation.${index}.address2`,
                                  );
                                  const touch = getIn(
                                    form.touched,
                                    `additionalLocation.${index}.address2`,
                                  );
                                  return (
                                    <div>
                                      <FormInputField
                                        name={`additionalLocation.${index}.address2`}
                                        invalid={!!touch && !!error}
                                        label="Address (line 2)"
                                        disabled={isAdditionalLocationDisabled(location)}
                                        labelVariant="bordered"
                                      />
                                      {touch && error ? (
                                        <label className="error-label">{error}</label>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              </Field>
                            </Col>
                            <Col md={6} lg={4}>
                              <Field name={`additionalLocation.${index}.city`}>
                                {({ form }) => {
                                  const error = getIn(
                                    form.errors,
                                    `additionalLocation.${index}.city`,
                                  );
                                  const touch = getIn(
                                    form.touched,
                                    `additionalLocation.${index}.city`,
                                  );
                                  return (
                                    <div>
                                      <FormInputField
                                        name={`additionalLocation.${index}.city`}
                                        invalid={!!touch && !!error}
                                        label="Town"
                                        disabled={isAdditionalLocationDisabled(location)}
                                        labelVariant="bordered"
                                        required
                                      />
                                      {touch && error ? (
                                        <label className="error-label">{error}</label>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              </Field>
                            </Col>
                            <Col md={6} lg={4}>
                              <Field name={`additionalLocation.${index}.county`}>
                                {({ form }) => {
                                  const error = getIn(
                                    form.errors,
                                    `additionalLocation.${index}.county`,
                                  );
                                  const touch = getIn(
                                    form.touched,
                                    `additionalLocation.${index}.county`,
                                  );
                                  return (
                                    <div>
                                      <FormInputField
                                        name={`additionalLocation.${index}.county`}
                                        invalid={!!touch && !!error}
                                        label="County"
                                        disabled={isAdditionalLocationDisabled(location)}
                                        labelVariant="bordered"
                                        required
                                      />
                                      {touch && error ? (
                                        <label className="error-label">{error}</label>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              </Field>
                            </Col>
                            <Col md={6} lg={4}>
                              <Field name={`additionalLocation.${index}.postalCode`}>
                                {({ form }) => {
                                  const error = getIn(
                                    form.errors,
                                    `additionalLocation.${index}.postalCode`,
                                  );
                                  const touch = getIn(
                                    form.touched,
                                    `additionalLocation.${index}.postalCode`,
                                  );
                                  return (
                                    <div>
                                      <PostcodeSelectField
                                        name={`additionalLocation.${index}.postalCode`}
                                        loadRightAway={false}
                                        label="Postal Code"
                                        disabled={isAdditionalLocationDisabled(location)}
                                        invalid={!!touch && !!error}
                                        labelVariant="bordered"
                                      />

                                      {touch && error ? (
                                        <label className="error-label">{error}</label>
                                      ) : null}
                                    </div>
                                  );
                                }}
                              </Field>
                            </Col>

                            {isOwner && (
                              <Col md={12} lg={4}>
                                <span className="delete-address-tooltip">
                                  <CommonButton
                                    className={`text-sm w-100 ${!location.canBeDeleted ? 'delete-address-btn--disabled' : ''}`}
                                    label="Delete Address"
                                    variant={CommonButtonVariants.OUTLINED_RED}
                                    handleClick={() => setIdDelLocation(index)}
                                    icon={trashIcon}
                                    iconLeft
                                    disabled={!location.canBeDeleted}
                                    buttonProperties={
                                      !location.canBeDeleted
                                        ? { style: { pointerEvents: 'none' } }
                                        : {}
                                    }
                                    iconAlt="Delete Address"
                                    type="button"
                                  />
                                  {!location.canBeDeleted && (
                                    <span
                                      className="delete-address-tooltip__bubble"
                                      role="tooltip"
                                    >
                                      This location cannot be deleted because it is associated
                                      with existing auctions, bids or transactions.
                                    </span>
                                  )}
                                </span>
                              </Col>
                            )}
                          </Row>
                        </Col>
                        <CommonSweetAlert
                          show={index === idDelLocation}
                          variant={CommonSweetAlertVariants.PRIMARY_RED}
                          onConfirm={() => {
                            handleDeleteAdditionalLocation(location, arrayHelpers, index);
                            setIdDelLocation(null);
                          }}
                          onCancel={() => setIdDelLocation(null)}
                          confirmButtonText="DELETE"
                          className="informations-form__sweet-alert"
                          noIcon
                        >
                          <button
                            type="button"
                            className="informations-form__sweet-alert-close informations-form__sweet-alert-close--modal"
                            aria-label="Close"
                            onClick={() => setIdDelLocation(null)}
                          >
                            <img src={closeIcon} alt="Close" width={30} height={30} />
                          </button>
                          <SettingsSectionHeader
                            header="Delete Address"
                            variant="noBorder"
                            headerClassName="informations-form__sweet-alert-header"
                          />
                          <p className="common-sweet-alert__title">
                            Please confirm that you want to delete{' '}
                            <b>Location Address {index + 1}</b>
                          </p>
                        </CommonSweetAlert>
                      </Fragment>
                    ))}
                  </div>
                </Row>
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4 settings-buttons-row">
          <Col md={6} lg={4}>
            <CommonButton
              className="text-sm w-100"
              label="SAVE CHANGES"
              disabled={localLoading || !isOwner}
              variant={CommonButtonVariants.PRIMARY_RED}
              handleClick={handleSaveOrganizationDetails}
              type="submit"
            />
          </Col>
          <Col md={6} lg={4}>
            <CommonButton
              className="text-sm w-100"
              label="ADD ANOTHER ADDRESS"
              variant={CommonButtonVariants.OUTLINED_RED}
              icon={redPlusIcon}
              iconAlt="Add New Location"
              iconLeft={true}
              disabled={!isOwner}
              handleClick={toggleModal}
              type="button"
            />
          </Col>
        </Row>
      </Form>
      <AddAddressModal isOpen={isModalOpen} toggle={toggleModal} onSave={handleSaveAddress} />
    </div>
  );
}

InformationsForm.propTypes = {
  values: PropTypes.object.isRequired,
  setSubmitting: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

export default InformationsForm;
